import React, { useState, useMemo } from 'react';
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Info, 
  Wrench, 
  Sparkles, 
  Search, 
  Check, 
  Database, 
  QrCode, 
  Barcode, 
  Calendar, 
  Hash, 
  FileText, 
  Layers, 
  Trash2, 
  ShieldCheck, 
  Zap,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Eraser,
  AlertCircle,
  Plus,
  Edit2
} from 'lucide-react';
import { CSVData, VdpTemplate } from '../../types';
import { useToast } from '../../store/ToastContext';
import { 
  ValidationReport, 
  ValidationIssue, 
  ColumnStat,
  applyCsvQuickFix, 
  autoCleanAllIssues 
} from '../../utils/csvValidation';
import { CsvFormatControls, FormatMeta } from './CsvFormatControls';
import { VisualDataValidationLayer } from './VisualDataValidationLayer';

export type { FormatMeta };

interface CsvValidationPanelProps {
  report: ValidationReport;
  csvData: CSVData;
  template: VdpTemplate;
  onUpdateCsv: (data: CSVData) => void;
  onProceed: () => void;
  formatMeta?: FormatMeta | null;
  selectedDelimiter?: string;
  selectedHeaderRow?: number;
  hasHeaderOverride?: boolean;
  onFormatChange?: (options: { delimiter?: string; headerRowIndex?: number; hasHeader?: boolean }) => void;
  fileName?: string | null;
}

export type PreviewFilterType = 'all' | 'errors_only' | 'missing_mapped' | 'empty_any' | 'empty_all' | 'valid_only';

export const CsvValidationPanel: React.FC<CsvValidationPanelProps> = ({
  report,
  csvData,
  template,
  onUpdateCsv,
  onProceed,
  formatMeta,
  selectedDelimiter = 'auto',
  selectedHeaderRow = -99,
  hasHeaderOverride,
  onFormatChange,
  fileName
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'issues' | 'schema' | 'preview'>('issues');
  const [issueFilter, setIssueFilter] = useState<'all' | 'error' | 'warning' | 'info'>('all');
  const [showPreflightModal, setShowPreflightModal] = useState(false);
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);

  // Preview Grid Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState<string>('all');
  const [previewFilter, setPreviewFilter] = useState<PreviewFilterType>('all');
  const [columnEmptyFilter, setColumnEmptyFilter] = useState<string>('all');
  const [pageSize, setPageSize] = useState<number>(15);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Table Inline Editing & Schema Modification States
  const [editingCell, setEditingCell] = useState<{ originalIndex: number; header: string } | null>(null);
  const [editVal, setEditVal] = useState<string>('');
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newColName, setNewColName] = useState('');

  // Save cell edit
  const handleSaveCell = (originalIndex: number, header: string, value: string) => {
    const updatedRows = csvData.rows.map((row, idx) => {
      if (idx === originalIndex) {
        return { ...row, [header]: value };
      }
      return row;
    });
    onUpdateCsv({ ...csvData, rows: updatedRows });
    setEditingCell(null);
  };

  // Add new record / row
  const handleAddRow = () => {
    const newRow: Record<string, string> = {};
    csvData.headers.forEach(h => {
      newRow[h] = '';
    });
    const updatedRows = [...csvData.rows, newRow];
    onUpdateCsv({ ...csvData, rows: updatedRows });
    toast.success('Record Added', `Added record #${updatedRows.length}. Click any cell to edit.`);
  };

  // Add new variable column / header
  const handleAddColumn = () => {
    const trimmed = newColName.trim();
    if (!trimmed) {
      toast.warning('Invalid Column Name', 'Please enter a valid column name.');
      return;
    }
    if (csvData.headers.includes(trimmed)) {
      toast.error('Column Exists', `Column "${trimmed}" already exists.`);
      return;
    }
    const updatedHeaders = [...csvData.headers, trimmed];
    const updatedRows = csvData.rows.map(r => ({ ...r, [trimmed]: '' }));
    onUpdateCsv({ headers: updatedHeaders, rows: updatedRows });
    setShowAddColumnModal(false);
    setNewColName('');
    toast.success('Column Added', `Added column "${trimmed}". You can now link elements to it or edit values.`);
  };

  // Delete variable column
  const handleDeleteColumn = (header: string) => {
    if (csvData.headers.length <= 1) {
      toast.warning('Cannot Delete Column', 'Database must have at least one column.');
      return;
    }
    const updatedHeaders = csvData.headers.filter(h => h !== header);
    const updatedRows = csvData.rows.map(r => {
      const copy = { ...r };
      delete copy[header];
      return copy;
    });
    onUpdateCsv({ headers: updatedHeaders, rows: updatedRows });
    toast.success('Column Removed', `Deleted column "${header}" from dataset.`);
  };

  const filteredIssues = report.issues.filter(issue => {
    if (issueFilter === 'all') return true;
    return issue.severity === issueFilter;
  });

  // Calculate row status statistics for the preview table
  const rowStats = useMemo(() => {
    let completelyBlankCount = 0;
    let anyEmptyCount = 0;
    let missingMappedCount = 0;
    const mappedCols = report.mappedColumns || [];

    csvData.rows.forEach(row => {
      const values = Object.values(row);
      const isCompletelyBlank = values.every(v => v === undefined || v === null || String(v).trim() === '');
      const hasAnyEmpty = csvData.headers.some(h => {
        const val = row[h];
        return val === undefined || val === null || String(val).trim() === '';
      });
      const hasMissingMapped = mappedCols.some(col => {
        const val = row[col];
        return val === undefined || val === null || String(val).trim() === '';
      });

      if (isCompletelyBlank) completelyBlankCount++;
      if (hasAnyEmpty) anyEmptyCount++;
      if (hasMissingMapped) missingMappedCount++;
    });

    return {
      total: csvData.rows.length,
      completelyBlankCount,
      anyEmptyCount,
      missingMappedCount
    };
  }, [csvData, report.mappedColumns]);

  // Filter and search dataset rows for the Data Grid Preview
  const { filteredRowsWithIndices, totalFilteredCount } = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const mappedCols = report.mappedColumns || [];

    const results: { row: Record<string, string | number>; originalIndex: number }[] = [];

    csvData.rows.forEach((row, originalIndex) => {
      const values = Object.values(row);
      const isCompletelyBlank = values.every(v => v === undefined || v === null || String(v).trim() === '');
      const hasAnyEmpty = csvData.headers.some(h => {
        const val = row[h];
        return val === undefined || val === null || String(val).trim() === '';
      });
      const hasMissingMapped = mappedCols.some(col => {
        const val = row[col];
        return val === undefined || val === null || String(val).trim() === '';
      });

      const hasCellError = Object.keys(report.cellErrors || {}).some(col => report.cellErrors[col]?.includes(originalIndex));

      // 1. Filter condition check
      if (previewFilter === 'errors_only' && !hasCellError && !isCompletelyBlank) return;
      if (previewFilter === 'empty_all' && !isCompletelyBlank) return;
      if (previewFilter === 'empty_any' && !hasAnyEmpty) return;
      if (previewFilter === 'missing_mapped' && !hasMissingMapped) return;
      if (previewFilter === 'valid_only' && (isCompletelyBlank || hasMissingMapped || hasCellError)) return;

      // 2. Specific column empty filter
      if (columnEmptyFilter !== 'all') {
        const colVal = row[columnEmptyFilter];
        const isColEmpty = colVal === undefined || colVal === null || String(colVal).trim() === '';
        if (!isColEmpty) return;
      }

      // 3. Search query check
      if (term) {
        if (searchColumn === 'all') {
          const match = Object.values(row).some(v => 
            String(v ?? '').toLowerCase().includes(term)
          );
          if (!match) return;
        } else {
          const colVal = String(row[searchColumn] ?? '').toLowerCase();
          if (!colVal.includes(term)) return;
        }
      }

      results.push({ row, originalIndex });
    });

    return {
      filteredRowsWithIndices: results,
      totalFilteredCount: results.length
    };
  }, [csvData, searchTerm, searchColumn, previewFilter, columnEmptyFilter, report.mappedColumns]);

  // Paginated slice
  const paginatedRows = useMemo(() => {
    if (pageSize === -1) return filteredRowsWithIndices;
    const startIndex = (currentPage - 1) * pageSize;
    return filteredRowsWithIndices.slice(startIndex, startIndex + pageSize);
  }, [filteredRowsWithIndices, currentPage, pageSize]);

  const totalPages = pageSize === -1 ? 1 : Math.ceil(totalFilteredCount / pageSize) || 1;

  // Handle single row deletion
  const handleDeleteRow = (originalIndex: number) => {
    const newRows = csvData.rows.filter((_, idx) => idx !== originalIndex);
    onUpdateCsv({
      ...csvData,
      rows: newRows
    });
    toast.success('Row Deleted', `Removed record #${originalIndex + 1} from the dataset.`);
  };

  // Handle bulk removal of 100% blank rows
  const handleRemoveBlankRows = () => {
    const blankCount = rowStats.completelyBlankCount;
    if (blankCount === 0) {
      toast.info('No Blank Rows', 'There are no 100% empty rows in your dataset.');
      return;
    }
    const cleanedRows = csvData.rows.filter(row => {
      return Object.values(row).some(v => v !== undefined && v !== null && String(v).trim() !== '');
    });
    onUpdateCsv({
      ...csvData,
      rows: cleanedRows
    });
    setPreviewFilter('all');
    toast.success('Blank Rows Removed', `Removed ${blankCount} empty ${blankCount === 1 ? 'row' : 'rows'} from the dataset.`);
  };

  // Handle bulk removal of incomplete / missing mapped records
  const handleRemoveMissingMappedRows = () => {
    const count = rowStats.missingMappedCount;
    if (count === 0) {
      toast.info('No Incomplete Records', 'All records have valid values for mapped template columns.');
      return;
    }
    const mappedCols = report.mappedColumns || [];
    const cleanedRows = csvData.rows.filter(row => {
      return !mappedCols.some(col => {
        const val = row[col];
        return val === undefined || val === null || String(val).trim() === '';
      });
    });
    onUpdateCsv({
      ...csvData,
      rows: cleanedRows
    });
    setPreviewFilter('all');
    toast.success('Incomplete Records Cleaned', `Removed ${count} records with missing required mapped values.`);
  };

  // Clear all filters & search
  const handleResetFilters = () => {
    setSearchTerm('');
    setSearchColumn('all');
    setPreviewFilter('all');
    setColumnEmptyFilter('all');
    setCurrentPage(1);
  };

  const handleFixIssue = (issue: ValidationIssue) => {
    if (issue.id === 'potential_tab_delimiter' && onFormatChange) {
      onFormatChange({ delimiter: '\t' });
      toast.info('Delimiter Changed', 'Switched CSV parser delimiter to Tab (\\t).');
      return;
    }
    if (issue.id === 'potential_semicolon_delimiter' && onFormatChange) {
      onFormatChange({ delimiter: ';' });
      toast.info('Delimiter Changed', 'Switched CSV parser delimiter to Semicolon (;).');
      return;
    }
    if (issue.id === 'potential_pipe_delimiter' && onFormatChange) {
      onFormatChange({ delimiter: '|' });
      toast.info('Delimiter Changed', 'Switched CSV parser delimiter to Pipe (|).');
      return;
    }
    const updated = applyCsvQuickFix(csvData, issue, template);
    onUpdateCsv(updated);
    toast.success(
      'Quick Fix Applied',
      issue.fixLabel || issue.title || 'Updated dataset records.'
    );
  };

  const handleAutoCleanAll = () => {
    const updated = autoCleanAllIssues(csvData, template);
    onUpdateCsv(updated);
    toast.success(
      'Dataset Auto-Cleaned',
      'All automated fixes (trimmed whitespace, removed empty rows, fixed headers) have been applied successfully.'
    );
  };

  const handleContinueClick = () => {
    if (report.hasErrors) {
      setShowPreflightModal(true);
    } else if (report.hasWarnings) {
      setShowPreflightModal(true);
    } else {
      onProceed();
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'barcode':
        return <Barcode className="w-3.5 h-3.5 text-indigo-500" />;
      case 'url':
        return <QrCode className="w-3.5 h-3.5 text-blue-500" />;
      case 'date':
        return <Calendar className="w-3.5 h-3.5 text-amber-500" />;
      case 'number':
        return <Hash className="w-3.5 h-3.5 text-blue-500" />;
      default:
        return <FileText className="w-3.5 h-3.5 text-zinc-500" />;
    }
  };

  const errorCount = report.issues.filter(i => i.severity === 'error').length;
  const warningCount = report.issues.filter(i => i.severity === 'warning').length;
  const infoCount = report.issues.filter(i => i.severity === 'info').length;

  const totalCellErrorsCount = useMemo(() => {
    const errorIndices = new Set<number>();
    Object.values(report.cellErrors || {}).forEach((indices) => {
      (indices as number[])?.forEach((idx: number) => errorIndices.add(idx));
    });
    return errorIndices.size;
  }, [report.cellErrors]);

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* 1. Visual Data Validation Layer */}
      <VisualDataValidationLayer
        csvData={csvData}
        template={template}
        validationReport={report}
        onApplyFix={handleFixIssue}
        onAutoCleanAll={handleAutoCleanAll}
      />

      {/* 2. Pre-flight Status & Health Hero Card */}
      <div className={`p-6 rounded-md border transition-all ${
        report.hasErrors 
          ? 'bg-red-950/40 border-red-900/50' 
          : report.hasWarnings 
            ? 'bg-amber-950/40 border-amber-900/50' 
            : 'bg-emerald-950/40 border-emerald-900/50'
      }`}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-md flex items-center justify-center shrink-0 shadow-sm ${
              report.hasErrors 
                ? 'bg-red-600 text-white' 
                : report.hasWarnings 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-[#1473E6] text-white'
            }`}>
              {report.hasErrors ? (
                <XCircle className="w-8 h-8" />
              ) : report.hasWarnings ? (
                <AlertTriangle className="w-8 h-8" />
              ) : (
                <ShieldCheck className="w-8 h-8" />
              )}
            </div>

            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h3 className="text-xl font-bold text-zinc-100">
                  {report.hasErrors 
                    ? 'CSV Structure Issues Require Resolution' 
                    : report.hasWarnings 
                      ? 'CSV Validated with Recommended Improvements' 
                      : 'CSV Structure 100% Validated & Production-Ready'}
                </h3>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                  report.hasErrors 
                    ? 'bg-red-950 text-red-300 border border-red-800' 
                    : report.hasWarnings 
                      ? 'bg-amber-950 text-amber-300 border border-amber-800' 
                      : 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                }`}>
                  Health Score: {report.healthScore}%
                </span>
              </div>
              <p className="text-sm text-zinc-400 mt-1">
                {report.hasErrors 
                  ? `${errorCount} critical blocking error(s) must be fixed before entering the template builder.` 
                  : report.hasWarnings 
                    ? `${warningCount} warning(s) detected. You can auto-repair them or proceed directly.` 
                    : `All ${report.totalRows} records and ${report.totalCols} columns passed structure, integrity, and binding checks.`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            {(report.hasErrors || report.hasWarnings) && (
              <button
                onClick={handleAutoCleanAll}
                className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 border border-zinc-700 hover:border-blue-600 hover:text-blue-600 text-zinc-300 font-semibold rounded-md text-sm shadow-xs transition-all cursor-pointer"
                title="Automatically fix missing headers, trim whitespace, and generate missing template columns"
              >
                <Sparkles className="w-4 h-4 text-amber-500" />
                Auto-Repair All
              </button>
            )}

            <button
              onClick={handleContinueClick}
              disabled={report.hasErrors}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-md font-bold text-sm shadow-md transition-all ${
                report.hasErrors
                  ? 'bg-zinc-300 text-zinc-500 cursor-not-allowed'
                  : 'bg-[#1473E6] hover:bg-blue-700 text-white cursor-pointer'
              }`}
            >
              <Zap className="w-4 h-4" />
              Continue to Design
            </button>
          </div>
        </div>

        {/* Metric Badges Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-zinc-800/70">
          <div className="bg-zinc-900/80 backdrop-blur-xs p-3 rounded-md border border-zinc-800/80 flex flex-col">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Total Records</span>
            <span className="text-xl font-bold text-zinc-200 mt-0.5">{report.totalRows}</span>
            <span className="text-[11px] text-zinc-500">Stickers to print</span>
          </div>

          <div className="bg-zinc-900/80 backdrop-blur-xs p-3 rounded-md border border-zinc-800/80 flex flex-col">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Total Columns</span>
            <span className="text-xl font-bold text-zinc-200 mt-0.5">{report.totalCols}</span>
            <span className="text-[11px] text-zinc-500">Available variables</span>
          </div>

          <div className="bg-zinc-900/80 backdrop-blur-xs p-3 rounded-md border border-zinc-800/80 flex flex-col">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Mapped to Template</span>
            <span className="text-xl font-bold text-blue-600 mt-0.5">{report.mappedColumns.length}</span>
            <span className="text-[11px] text-zinc-500">{report.missingMappedColumns.length > 0 ? `${report.missingMappedColumns.length} unmapped` : 'All connected'}</span>
          </div>

          <div className="bg-zinc-900/80 backdrop-blur-xs p-3 rounded-md border border-zinc-800/80 flex flex-col">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Data Density</span>
            <span className="text-xl font-bold text-zinc-200 mt-0.5">
              {report.totalRows > 0 ? Math.round(((report.totalRows * report.totalCols - report.blankCellsCount) / (report.totalRows * report.totalCols)) * 100) : 0}%
            </span>
            <span className="text-[11px] text-zinc-500">{report.blankCellsCount} blank cells</span>
          </div>
        </div>
      </div>

      {/* Format Detection & Delimiter / Header Settings Bar */}
      {formatMeta && onFormatChange && (
        <CsvFormatControls
          formatMeta={formatMeta}
          selectedDelimiter={selectedDelimiter}
          selectedHeaderRow={selectedHeaderRow}
          hasHeaderOverride={hasHeaderOverride}
          onFormatChange={onFormatChange}
          fileName={fileName}
          totalRecordsCount={report.totalRows}
          totalColumnsCount={report.totalCols}
        />
      )}

      {/* 2. Navigation Tabs */}
      <div className="flex items-center justify-between border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('issues')}
            className={`flex items-center gap-2 py-3 px-4 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
              activeTab === 'issues'
                ? 'border-orange-600 text-blue-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-200'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Validation Issues & Checks
            {report.issues.length > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                report.hasErrors ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
              }`}>
                {report.issues.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('schema')}
            className={`flex items-center gap-2 py-3 px-4 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
              activeTab === 'schema'
                ? 'border-orange-600 text-blue-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-200'
            }`}
          >
            <Layers className="w-4 h-4" />
            Schema & Column Inspector
            <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full font-medium">
              {report.totalCols}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('preview')}
            id="tab-data-grid-preview"
            className={`flex items-center gap-2 py-3 px-4 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
              activeTab === 'preview'
                ? 'border-orange-600 text-blue-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-200'
            }`}
          >
            <Database className="w-4 h-4" />
            Data Grid Preview
            {rowStats.missingMappedCount > 0 ? (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {rowStats.missingMappedCount} empty
              </span>
            ) : rowStats.completelyBlankCount > 0 ? (
              <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-bold">
                {rowStats.completelyBlankCount} blank
              </span>
            ) : (
              <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full font-medium">
                {csvData.rows.length} rows
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Tab 1: Issues & Diagnostics */}
      {activeTab === 'issues' && (
        <div className="flex flex-col gap-5">
          {/* Issue Filters */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Filter:</span>
              <button
                onClick={() => setIssueFilter('all')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                  issueFilter === 'all' ? 'bg-zinc-800 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                All ({report.issues.length})
              </button>
              <button
                onClick={() => setIssueFilter('error')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                  issueFilter === 'error' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'
                }`}
              >
                Errors ({errorCount})
              </button>
              <button
                onClick={() => setIssueFilter('warning')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                  issueFilter === 'warning' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                }`}
              >
                Warnings ({warningCount})
              </button>
              <button
                onClick={() => setIssueFilter('info')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer ${
                  issueFilter === 'info' ? 'bg-[#1473E6] text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
              >
                Info ({infoCount})
              </button>
            </div>

            {report.issues.length > 0 && (
              <span className="text-xs text-zinc-500">
                Click "Quick Fix" on any issue to automatically resolve it.
              </span>
            )}
          </div>

          {/* Issue Cards */}
          {filteredIssues.length > 0 ? (
            <div className="flex flex-col gap-3">
              {filteredIssues.map((issue) => {
                const isExpanded = expandedIssueId === issue.id;
                return (
                  <div
                    key={issue.id}
                    className={`p-4 rounded-md border transition-all ${
                      issue.severity === 'error'
                        ? 'bg-red-950/40 border-red-900/50'
                        : issue.severity === 'warning'
                          ? 'bg-amber-950/40 border-amber-900/50'
                          : 'bg-blue-950/40 border-blue-900/50'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 shrink-0">
                          {issue.severity === 'error' ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : issue.severity === 'warning' ? (
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                          ) : (
                            <Info className="w-5 h-5 text-blue-500" />
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-sm text-zinc-100">{issue.title}</h4>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider ${
                              issue.severity === 'error'
                                ? 'bg-red-950 text-red-300 border border-red-800'
                                : issue.severity === 'warning'
                                  ? 'bg-amber-950 text-amber-300 border border-amber-800'
                                  : 'bg-blue-950 text-blue-300 border border-blue-800'
                            }`}>
                              {issue.category.replace('_', ' ')}
                            </span>
                            {issue.columnName && (
                              <span className="text-[11px] font-mono bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800 text-zinc-300 font-medium">
                                Col: {issue.columnName}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{issue.message}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        {issue.fixAction && (
                          <button
                            onClick={() => handleFixIssue(issue)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 border border-zinc-700 hover:border-orange-600 hover:text-blue-600 text-zinc-300 text-xs font-semibold rounded-md shadow-2xs transition-all cursor-pointer"
                          >
                            <Wrench className="w-3.5 h-3.5 text-blue-500" />
                            {issue.fixLabel || 'Quick Fix'}
                          </button>
                        )}
                        {(issue.sampleValues || issue.rowIndices) && (
                          <button
                            onClick={() => setExpandedIssueId(isExpanded ? null : issue.id)}
                            className="p-1.5 text-zinc-400 hover:text-zinc-400 rounded hover:bg-zinc-900/60 transition-colors"
                            title="Toggle Details"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expandable Details Area */}
                    {isExpanded && (issue.sampleValues || issue.rowIndices) && (
                      <div className="mt-3 pt-3 border-t border-zinc-800/60 text-xs text-zinc-400 flex flex-col gap-1.5">
                        {issue.rowIndices && (
                          <div>
                            <span className="font-semibold text-zinc-300">Affected Record Indices: </span>
                            {issue.rowIndices.slice(0, 10).map(r => `#${r + 1}`).join(', ')}
                            {issue.rowIndices.length > 10 && ` and ${issue.rowIndices.length - 10} more...`}
                          </div>
                        )}
                        {issue.sampleValues && (
                          <div>
                            <span className="font-semibold text-zinc-300">Sample Items: </span>
                            <span className="font-mono bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800">
                              {issue.sampleValues.join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center bg-emerald-50/40 border border-emerald-200 rounded-md">
              <CheckCircle2 className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <h4 className="font-bold text-zinc-200">No {issueFilter !== 'all' ? issueFilter : ''} issues found</h4>
              <p className="text-xs text-zinc-500 mt-1">Your dataset meets all validation criteria in this category.</p>
            </div>
          )}

          {/* Passed Checks Section */}
          {report.passedChecks.length > 0 && (
            <div className="mt-4 bg-zinc-900 border border-zinc-800 rounded-md p-5 shadow-2xs">
              <h4 className="font-bold text-sm text-zinc-200 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                Passed Integrity Checks ({report.passedChecks.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {report.passedChecks.map((check) => (
                  <div key={check.id} className="flex items-start gap-2.5 p-2.5 rounded-md bg-zinc-950 border border-zinc-800">
                    <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" strokeWidth={2.5} />
                    <div>
                      <span className="text-xs font-semibold text-zinc-200">{check.name}</span>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{check.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Schema & Column Inspector */}
      {activeTab === 'schema' && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(Object.values(report.columnStats) as ColumnStat[]).map((stat) => (
              <div
                key={stat.name}
                className={`p-4 rounded-md border bg-zinc-900 shadow-2xs flex flex-col justify-between ${
                  stat.isMapped ? 'border-orange-300 ring-1 ring-blue-100' : 'border-zinc-800'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 truncate">
                      {getTypeIcon(stat.inferredType)}
                      <h4 className="font-bold text-sm text-zinc-200 truncate" title={stat.name}>
                        {stat.name}
                      </h4>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 capitalize">
                        {stat.inferredType}
                      </span>
                      {stat.isMapped && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Mapped
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Empty Rate Progress */}
                  <div className="mt-3">
                    <div className="flex justify-between text-[11px] font-medium text-zinc-500 mb-1">
                      <span>Fill Rate: {100 - stat.emptyPercentage}%</span>
                      <span>{stat.uniqueCount} Unique Values</span>
                    </div>
                    <div className="w-full bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          stat.emptyPercentage > 30 ? 'bg-amber-500' : 'bg-[#1473E6]'
                        }`}
                        style={{ width: `${Math.max(5, 100 - stat.emptyPercentage)}%` }}
                      />
                    </div>
                  </div>

                  {/* Sample Values */}
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider block mb-1">
                      Sample Values:
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {stat.sampleValues.length > 0 ? (
                        stat.sampleValues.map((val, idx) => (
                          <span
                            key={idx}
                            className="text-[11px] font-mono bg-zinc-950 px-1.5 py-0.5 rounded border border-zinc-800 text-zinc-400 truncate max-w-[120px]"
                            title={val}
                          >
                            {val}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-zinc-400 italic">No values (Empty column)</span>
                      )}
                    </div>
                  </div>
                </div>

                {stat.invalidFormatCount && stat.invalidFormatCount > 0 ? (
                  <div className="mt-3 text-[11px] font-medium text-amber-700 bg-amber-50 p-2 rounded-md border border-amber-200 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    {stat.invalidFormatCount} invalid format values detected
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 3: Data Grid Preview with Advanced Search & Filter */}
      {activeTab === 'preview' && (
        <div className="flex flex-col gap-4">
          {/* Action & Filter Toolbar */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-md shadow-2xs flex flex-col gap-3">
            {/* Top row: Search input, Column selector, and Page size */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              {/* Search Bar */}
              <div className="flex items-center gap-2 flex-1 max-w-xl">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -tranzinc-y-1/2" />
                  <input
                    type="text"
                    id="input-preview-search"
                    placeholder={`Search ${searchColumn === 'all' ? 'across all columns' : `in "${searchColumn}"`}...`}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-9 pr-8 py-2 bg-zinc-950 hover:bg-zinc-800/70 focus:bg-zinc-900 border border-zinc-800 focus:border-blue-600 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                      className="absolute right-2.5 top-1/2 -tranzinc-y-1/2 text-zinc-400 hover:text-zinc-400 p-0.5 rounded cursor-pointer"
                      title="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Column Target Selector */}
                <select
                  value={searchColumn}
                  onChange={(e) => {
                    setSearchColumn(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-xs font-semibold text-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer shrink-0"
                  title="Filter search to specific column"
                >
                  <option value="all">All Columns</option>
                  {csvData.headers.map((h) => (
                    <option key={h} value={h}>
                      Col: {h}
                    </option>
                  ))}
                </select>
              </div>

              {/* Page Size & Clear Filters */}
              <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                {(searchTerm || previewFilter !== 'all' || columnEmptyFilter !== 'all') && (
                  <button
                    onClick={handleResetFilters}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md transition-colors cursor-pointer"
                    title="Reset all search queries and filters"
                  >
                    <Eraser className="w-3.5 h-3.5 text-zinc-400" />
                    Reset Filters
                  </button>
                )}

                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Per Page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                    className="px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-md text-xs font-semibold text-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer"
                  >
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={-1}>All ({csvData.rows.length})</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bottom row: Filter chips and bulk cleaning actions */}
            <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2 border-t border-zinc-800">
              {/* Filter Pills */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mr-1 flex items-center gap-1">
                  <Filter className="w-3 h-3" />
                  Filter Rows:
                </span>

                <button
                  onClick={() => {
                    setPreviewFilter('all');
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                    previewFilter === 'all'
                      ? 'bg-zinc-900 text-white shadow-2xs'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <span>All Rows</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                    previewFilter === 'all' ? 'bg-zinc-700 text-zinc-200' : 'bg-zinc-900 text-zinc-400'
                  }`}>
                    {rowStats.total}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setPreviewFilter('errors_only');
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                    previewFilter === 'errors_only'
                      ? 'bg-red-600 text-white shadow-2xs'
                      : totalCellErrorsCount > 0
                        ? 'bg-red-950/70 text-red-300 hover:bg-red-900/70 border border-red-500/80 animate-pulse'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <XCircle className="w-3 h-3 text-red-400" />
                  <span>Errors & Mismatches</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                    previewFilter === 'errors_only' ? 'bg-red-800 text-white' : 'bg-red-950 text-red-300 border border-red-800'
                  }`}>
                    {totalCellErrorsCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setPreviewFilter('missing_mapped');
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                    previewFilter === 'missing_mapped'
                      ? 'bg-amber-600 text-white shadow-2xs'
                      : rowStats.missingMappedCount > 0
                        ? 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200/80'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <AlertTriangle className="w-3 h-3" />
                  <span>Missing Mapped Data</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                    previewFilter === 'missing_mapped' ? 'bg-amber-700 text-amber-100' : 'bg-amber-200/70 text-amber-900'
                  }`}>
                    {rowStats.missingMappedCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setPreviewFilter('empty_any');
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                    previewFilter === 'empty_any'
                      ? 'bg-[#1473E6] text-white shadow-2xs'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <span>Any Empty Cell</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                    previewFilter === 'empty_any' ? 'bg-blue-700 text-blue-100' : 'bg-zinc-900 text-zinc-400'
                  }`}>
                    {rowStats.anyEmptyCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setPreviewFilter('empty_all');
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                    previewFilter === 'empty_all'
                      ? 'bg-red-600 text-white shadow-2xs'
                      : rowStats.completelyBlankCount > 0
                        ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <span>100% Blank Rows</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                    previewFilter === 'empty_all' ? 'bg-red-700 text-red-100' : 'bg-red-200/70 text-red-900'
                  }`}>
                    {rowStats.completelyBlankCount}
                  </span>
                </button>

                {/* Specific Column Blank Filter */}
                <div className="flex items-center gap-1 ml-1">
                  <select
                    value={columnEmptyFilter}
                    onChange={(e) => {
                      setColumnEmptyFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer border ${
                      columnEmptyFilter !== 'all'
                        ? 'bg-blue-50 text-blue-800 border-orange-300 font-bold'
                        : 'bg-zinc-950 text-zinc-400 border-zinc-800'
                    }`}
                  >
                    <option value="all">Column is Empty: (None)</option>
                    {csvData.headers.map((h) => (
                      <option key={h} value={h}>
                        Empty in: {h}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bulk Cleaning Actions */}
              <div className="flex items-center gap-2">
                {rowStats.completelyBlankCount > 0 && (
                  <button
                    onClick={handleRemoveBlankRows}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-md text-xs font-bold transition-all shadow-2xs cursor-pointer"
                    title="Remove all completely empty rows across the dataset"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Clean {rowStats.completelyBlankCount} Blank {rowStats.completelyBlankCount === 1 ? 'Row' : 'Rows'}
                  </button>
                )}

                {rowStats.missingMappedCount > 0 && previewFilter === 'missing_mapped' && (
                  <button
                    onClick={handleRemoveMissingMappedRows}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-md text-xs font-bold transition-all shadow-2xs cursor-pointer"
                    title="Remove rows with missing values in required variable fields"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove {rowStats.missingMappedCount} Incomplete Records
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Results Summary Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-1 text-xs text-zinc-500 font-medium">
            <div>
              Showing <span className="font-bold text-zinc-200">{totalFilteredCount}</span> of <span className="font-bold text-zinc-200">{csvData.rows.length}</span> records
              {(searchTerm || previewFilter !== 'all' || columnEmptyFilter !== 'all') && (
                <span className="ml-2 text-blue-600 font-semibold">(Filtered)</span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleAddRow}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-md text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Record / Row
              </button>

              <button
                type="button"
                onClick={() => setShowAddColumnModal(true)}
                className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-md text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5 text-blue-400" />
                Add Column / Field
              </button>

              {totalPages > 1 && (
                <div className="flex items-center gap-2 ml-2">
                  <span>Page {currentPage} of {totalPages}</span>
                  <div className="flex items-center gap-1">
                    <button
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="p-1 rounded-md border border-zinc-800 bg-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-800 cursor-pointer"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <button
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className="p-1 rounded-md border border-zinc-800 bg-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-zinc-800 cursor-pointer"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto bg-zinc-900 border border-zinc-800 rounded-md shadow-2xs">
            <table className="w-full text-left border-collapse text-xs">
              <thead className="bg-zinc-950 border-b border-zinc-800 sticky top-0 z-10">
                <tr>
                  <th className="p-2.5 font-semibold text-zinc-400 border-r border-zinc-800 w-14 text-center">
                    #
                  </th>
                  {csvData.headers.map((header) => {
                    const isMapped = report.mappedColumns.includes(header);
                    const varValidation = report.templateVariables?.find(v => v.variableName === header);
                    const hasTypeMismatch = varValidation?.status === 'type_mismatch' || report.columnStats[header]?.hasTypeMismatch;
                    const hasErrorsInCol = Boolean(report.cellErrors?.[header]?.length);

                    return (
                      <th 
                        key={header} 
                        className={`p-2.5 font-semibold border-r border-zinc-800 whitespace-nowrap transition-colors group/head ${
                          hasTypeMismatch || hasErrorsInCol
                            ? 'bg-red-950/80 text-red-300 border-b-2 border-b-red-500' 
                            : 'text-zinc-300'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{header}</span>
                            {hasTypeMismatch ? (
                              <span className="bg-red-950 border border-red-500 text-red-300 text-[10px] px-1.5 py-0.2 rounded font-bold flex items-center gap-1 shadow-2xs">
                                🔴 Type Error
                              </span>
                            ) : isMapped ? (
                              <span className="bg-blue-900/60 border border-blue-500/50 text-blue-300 text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                                Mapped
                              </span>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleDeleteColumn(header)}
                            className="opacity-0 group-hover/head:opacity-100 p-1 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded transition-all cursor-pointer"
                            title={`Delete column "${header}"`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </th>
                    );
                  })}
                  <th className="p-2 font-semibold text-zinc-400 w-12 text-center bg-zinc-950/80">
                    <button
                      type="button"
                      onClick={() => setShowAddColumnModal(true)}
                      className="p-1 hover:bg-zinc-800 text-blue-400 rounded transition-colors cursor-pointer"
                      title="Add New Column"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </th>
                  <th className="p-2.5 font-semibold text-zinc-400 w-16 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map(({ row, originalIndex }) => {
                    const values = Object.values(row);
                    const isBlankRow = values.every(v => v === undefined || v === null || String(v).trim() === '');
                    const hasMissingMapped = (report.mappedColumns || []).some(col => {
                      const val = row[col];
                      return val === undefined || val === null || String(val).trim() === '';
                    });
                    const hasRowCellError = Object.keys(report.cellErrors || {}).some(col => report.cellErrors[col]?.includes(originalIndex));

                    return (
                      <tr 
                        key={originalIndex} 
                        className={`border-b border-zinc-800 last:border-0 transition-colors group ${
                          hasRowCellError
                            ? 'bg-red-950/30 hover:bg-red-950/50'
                            : isBlankRow 
                              ? 'bg-red-950/40 hover:bg-red-950/70' 
                              : hasMissingMapped 
                                ? 'bg-amber-950/30 hover:bg-amber-950/60' 
                                : 'hover:bg-zinc-800/80'
                        }`}
                      >
                        <td className="p-2.5 border-r border-zinc-800 text-zinc-400 text-center font-mono font-semibold">
                          <div className="flex items-center justify-center gap-1">
                            {hasRowCellError ? (
                              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Validation Error in Row" />
                            ) : isBlankRow ? (
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="Blank Row" />
                            ) : hasMissingMapped ? (
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Missing Mapped Field" />
                            ) : null}
                            <span>{originalIndex + 1}</span>
                          </div>
                        </td>

                        {csvData.headers.map((header) => {
                          const cellVal = row[header];
                          const isBlank = cellVal === undefined || cellVal === null || String(cellVal).trim() === '';
                          const isMapped = report.mappedColumns.includes(header);
                          const varValidation = report.templateVariables?.find(v => v.variableName === header);
                          const isCellError = report.cellErrors?.[header]?.includes(originalIndex);
                          const strVal = String(cellVal ?? '');
                          const isMatch = searchTerm && strVal.toLowerCase().includes(searchTerm.toLowerCase());
                          const isEditing = editingCell?.originalIndex === originalIndex && editingCell?.header === header;

                          return (
                            <td
                              key={header}
                              onClick={() => {
                                if (!isEditing) {
                                  setEditingCell({ originalIndex, header });
                                  setEditVal(strVal);
                                }
                              }}
                              className={`p-1.5 border-r border-zinc-800 font-mono transition-colors relative cursor-text group/cell ${
                                isEditing
                                  ? 'bg-zinc-950 ring-2 ring-blue-500 z-20'
                                  : isCellError
                                    ? 'bg-red-950/80 text-red-200 border-r-red-700/80 ring-1 ring-red-500/60 font-bold'
                                    : isBlank && isMapped 
                                      ? 'bg-amber-950/40 text-amber-300 font-semibold' 
                                      : isBlank 
                                        ? 'text-zinc-500 italic' 
                                        : isMatch
                                          ? 'bg-yellow-950/60 text-yellow-200 font-semibold'
                                          : 'text-zinc-300 hover:bg-zinc-800'
                              }`}
                              title="Click or double click to edit cell"
                            >
                              {isEditing ? (
                                <input
                                  type="text"
                                  autoFocus
                                  value={editVal}
                                  onChange={(e) => setEditVal(e.target.value)}
                                  onBlur={() => handleSaveCell(originalIndex, header, editVal)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSaveCell(originalIndex, header, editVal);
                                    } else if (e.key === 'Escape') {
                                      setEditingCell(null);
                                    }
                                  }}
                                  className="w-full px-2 py-1 bg-zinc-900 border border-blue-500 text-white rounded outline-none text-xs font-mono"
                                />
                              ) : isCellError ? (
                                <div className="flex items-center justify-between gap-1.5 px-1">
                                  {isBlank ? (
                                    <span className="text-red-400 font-bold flex items-center gap-1">
                                      ⚠ (Required Blank)
                                    </span>
                                  ) : (
                                    <>
                                      <span className="truncate text-red-200">{strVal}</span>
                                      <span className="text-[9px] bg-red-600 text-white px-1 py-0.2 rounded font-bold uppercase shrink-0">
                                        Invalid
                                      </span>
                                    </>
                                  )}
                                </div>
                              ) : isBlank ? (
                                <div className="flex items-center justify-between px-1">
                                  <span className={isMapped ? 'text-amber-400 flex items-center gap-1 font-medium' : 'text-zinc-500'}>
                                    {isMapped ? '⚠ (Empty)' : '—'}
                                  </span>
                                  <Edit2 className="w-3 h-3 text-zinc-600 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
                                </div>
                              ) : (
                                <div className="flex items-center justify-between px-1">
                                  <span className="truncate">{strVal}</span>
                                  <Edit2 className="w-3 h-3 text-zinc-500 opacity-0 group-hover/cell:opacity-100 transition-opacity shrink-0 ml-1" />
                                </div>
                              )}
                            </td>
                          );
                        })}

                        <td className="p-1.5 text-center bg-zinc-950/40"></td>

                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteRow(originalIndex)}
                            className="p-1.5 text-zinc-300 group-hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                            title={`Delete record #${originalIndex + 1}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={csvData.headers.length + 2} className="p-12 text-center text-zinc-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <AlertCircle className="w-8 h-8 text-zinc-300" />
                        <span className="font-bold text-sm text-zinc-400">No records matching your search & filter criteria</span>
                        <p className="text-xs text-zinc-400 max-w-sm">
                          Try searching for a different value, clearing the search term, or selecting "All Rows".
                        </p>
                        <button
                          onClick={handleResetFilters}
                          className="mt-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md text-xs font-bold transition-colors cursor-pointer"
                        >
                          Reset Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Bottom Pagination Bar */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800 rounded-md shadow-2xs">
              <span className="text-xs text-zinc-500 font-medium">
                Page <span className="font-bold text-zinc-200">{currentPage}</span> of <span className="font-bold text-zinc-200">{totalPages}</span> ({totalFilteredCount} records total)
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(1)}
                  className="px-2.5 py-1 text-xs font-semibold rounded-md border border-zinc-800 bg-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-800 cursor-pointer"
                >
                  First
                </button>
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  className="p-1.5 rounded-md border border-zinc-800 bg-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-800 cursor-pointer"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum = i + 1;
                    if (totalPages > 5) {
                      if (currentPage > 3) {
                        pageNum = Math.min(totalPages - 4 + i, currentPage - 2 + i);
                      }
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-7 h-7 text-xs font-bold rounded-md transition-colors cursor-pointer ${
                          currentPage === pageNum
                            ? 'bg-[#1473E6] text-white'
                            : 'bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  className="p-1.5 rounded-md border border-zinc-800 bg-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-800 cursor-pointer"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-2.5 py-1 text-xs font-semibold rounded-md border border-zinc-800 bg-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-800 cursor-pointer"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pre-flight Confirmation Dialog */}
      {showPreflightModal && (
        <div className="fixed inset-0 bg-zinc-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-md shadow-xl w-full max-w-lg overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
            <div className={`p-5 border-b flex items-center justify-between ${
              report.hasErrors ? 'bg-red-950/40 border-red-800/40' : 'bg-amber-950/40 border-amber-800/40'
            }`}>
              <div className="flex items-center gap-2.5">
                <div className="flex items-center gap-1.5 mr-2">
                  <button 
                    onClick={() => setShowPreflightModal(false)} 
                    className="w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 transition-colors cursor-pointer" 
                    title="Close Window" 
                  ></button>
                  <div className="w-3.5 h-3.5 rounded-full bg-yellow-500/50" />
                  <div className="w-3.5 h-3.5 rounded-full bg-green-500/50" />
                </div>
                {report.hasErrors ? (
                  <XCircle className="w-6 h-6 text-red-400" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-amber-400" />
                )}
                <h3 className="text-lg font-bold text-zinc-100">
                  {report.hasErrors ? 'Cannot Proceed: Validation Errors' : 'Pre-flight Validation Notice'}
                </h3>
              </div>
            </div>

            <div className="p-6 flex flex-col gap-4">
              <p className="text-sm text-zinc-400 leading-relaxed">
                {report.hasErrors
                  ? 'Your CSV dataset currently contains structural errors that will prevent correct variable substitution or barcode generation.'
                  : 'Your CSV dataset contains minor warnings (e.g. empty fields in mapped columns or missing template bindings). You can auto-repair them now or proceed with the current dataset.'}
              </p>

              <div className="max-h-48 overflow-y-auto flex flex-col gap-2 p-3 bg-zinc-950 rounded-md border border-zinc-800">
                {report.issues
                  .filter(i => report.hasErrors ? i.severity === 'error' : i.severity === 'warning')
                  .map((issue) => (
                    <div key={issue.id} className="text-xs flex items-start gap-2 text-zinc-300">
                      <span className="font-bold text-zinc-100">• {issue.title}:</span>
                      <span>{issue.message}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowPreflightModal(false)}
                className="px-4 py-2 text-zinc-400 font-semibold hover:bg-zinc-700 rounded-md text-sm transition-colors cursor-pointer"
              >
                Review & Edit
              </button>

              <button
                onClick={() => {
                  handleAutoCleanAll();
                  setShowPreflightModal(false);
                }}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-md text-sm shadow-sm transition-all cursor-pointer"
              >
                <Sparkles className="w-4 h-4" />
                Auto-Repair & Continue
              </button>

              {!report.hasErrors && (
                <button
                  onClick={() => {
                    setShowPreflightModal(false);
                    onProceed();
                  }}
                  className="px-5 py-2 bg-[#1473E6] hover:bg-blue-700 text-white font-semibold rounded-md text-sm shadow-md transition-all cursor-pointer"
                >
                  Proceed Anyway
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Add New Column Modal */}
      {showAddColumnModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
            <div className="p-4 border-b border-zinc-800 bg-zinc-950 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-400" />
                <h3 className="font-bold text-sm text-zinc-100">Add Variable Database Column</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddColumnModal(false)}
                className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">
                  Column / Field Header Name
                </label>
                <input
                  type="text"
                  autoFocus
                  placeholder="e.g. SerialNo, ExpiryDate, BatchCode, QrUrl"
                  value={newColName}
                  onChange={(e) => setNewColName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddColumn();
                    if (e.key === 'Escape') setShowAddColumnModal(false);
                  }}
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 text-white rounded-lg text-xs font-mono outline-none focus:ring-2 focus:ring-blue-600"
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  You can use this header as variable placeholder <code className="text-blue-400 font-mono">{`{${newColName || 'HeaderName'}}`}</code> in text elements or bind it to barcodes.
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddColumnModal(false)}
                className="px-3.5 py-1.5 text-zinc-400 font-semibold hover:bg-zinc-800 rounded-lg text-xs transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddColumn}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg text-xs transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Column
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
